<template>
    <LaBaseCard>
        <template #header>
            <div class="header">
                <LaCsvDownload class="header-button" :csv-data="blockRateData" :filename="filename" />
            </div>
        </template>

        <div v-if="!blockRateLoading" class="columns">
            <div class="column is-one-thirds is-flex is-justify-content-center" v-if="$mq.min.bigTablet">
                <BlockRateDonutChart
                    :allowed-percent="allowedPercent"
                    :blocking-percent="blockingPercent"
                    :allowed-pageviews-total="Number(allowedPageviewsTotal)"
                    :blocked-pageviews-total="Number(blockedPageviewsTotal)"
                />
            </div>
            <div class="column">
                <BlockRateStackedChart
                    :dates="lineLabels"
                    :allowed-pageviews="allowedData"
                    :blocked-pageviews="blockingData"
                />
            </div>
        </div>
        <LaDataCardLoader v-else row :chart-count="$mq.min.bigTablet ? 2 : 1" :chart-height="400" :table="false" />
    </LaBaseCard>
</template>

<script>
import LaBaseCard from "@/components/BaseCard";
import LaDataCardLoader from "@/components/loaders/DataCardLoader";
import LaCsvDownload from "@/components/CsvDownload";
import { reportFilenamePrefix, LTBA_PUBLISHER_GETTER } from "@/helpers/reportFilename";
import { formatDate, formatCell } from "@/helpers/filters.js";
import { getItem } from "@/store/persistantStorage";
import BlockRateDonutChart from "@/components/charts/BlockRateDonutChart.vue";
import BlockRateStackedChart from "@/components/charts/BlockRateStackedChart.vue";

export default {
    name: "LaBlockRateDataCard",
    components: {
        LaBaseCard,
        LaDataCardLoader,
        LaCsvDownload,
        BlockRateDonutChart,
        BlockRateStackedChart,
    },
    props: {
        blockRateLoading: {
            type: Boolean,
        },
        blockRateData: {
            type: Array,
            required: true,
        },
        blockRateMetadata: {
            type: Object,
            required: true,
        },
        domains: {
            type: Array,
            required: true,
        },
    },
    computed: {
        isLightTheme() {
            return getItem("theme") == "light";
        },
        filename() {
            // Several websites: name the file after the selected publisher instead of listing every domain
            if (this.domains.length > 1) {
                return `${reportFilenamePrefix(this.domains, LTBA_PUBLISHER_GETTER)}_%from>%to`;
            }
            return `${this.domains[0]}_blockRate_%from>%to`;
        },
        blockingPercent() {
            return this.blockRateMetadata.percent.blocking;
        },
        allowedPercent() {
            return this.blockRateMetadata.percent.non_blocking;
        },
        pieLabels() {
            return [
                "Allowed pageviews" + " (" + formatCell(this.allowedPageviewsTotal) + ")",
                "Blocked pageviews" + " (" + formatCell(this.blockedPageviewsTotal) + ")",
            ];
        },
        pieDatasetsLight() {
            return {
                label: "Pageviews",
                data: [this.allowedPercent, this.blockingPercent],
                backgroundColor: ["#1dce80", "#de350be6"],

                borderColor: ["#ffffff", "#ffffff"],
                borderWidth: 1,
            };
        },
        pieDatasetsDark() {
            return {
                label: "Pageviews",
                data: [this.allowedPercent, this.blockingPercent],
                backgroundColor: ["#0f2214", "#20140F"],
                borderColor: ["#a8ef4f", "#c10000"],
                borderWidth: 1,
                // cutout dowsn't work with vue3-chart-v2 cutout: "90%"
            };
        },
        blockingData() {
            return this.blockRateData.map((item) => item.blocking);
        },
        allowedData() {
            return this.blockRateData.map((item) => item["non_blocking"]);
        },
        blockedPageviewsTotal() {
            let total = 0;
            this.blockRateData.map((item) => {
                total += item.blocking;
            });
            return total;
        },
        allowedPageviewsTotal() {
            let total = 0;
            this.blockRateData.map((item) => {
                total += item.non_blocking;
            });
            return total;
        },
        lineLabels() {
            return this.blockRateData.map((item) => formatDate(new Date(item.date)));
        },
    },
};
</script>

<style lang="scss" scoped>
.graph-title {
    color: $blue;
    font-size: 2rem;
    margin-bottom: 0.5rem;
    &-sub {
        font-size: 0.9rem;
    }
}
.dd {
    width: 100%;
}

body.dark {
    .graph-title {
        color: $text;
    }
}
</style>
